import mixpanel from 'mixpanel-browser';

// Enhanced analytics service with Mixpanel and GTM integration
class AnalyticsService {
  constructor() {
    // Ensure dataLayer exists for GTM
    window.dataLayer = window.dataLayer || [];
    
    // Initialize Mixpanel
    this.mixpanelInitialized = false;
    this.initMixpanel();
    
    // Page tracking state
    this.currentPage = null;
    this.pageStartTime = null;
    this.pageVisitCounts = {};
    this.sessionStartTime = Date.now();
    this.pageHistory = [];
    
    // Initialize page tracking
    this.initPageTracking();
  }

  initMixpanel() {
    const mixpanelToken = process.env.REACT_APP_MIXPANEL_TOKEN;
    
    if (mixpanelToken && typeof window !== 'undefined') {
      try {
        mixpanel.init(mixpanelToken, {
          debug: process.env.REACT_APP_ENVIRONMENT === 'development',
          track_pageview: true,
          persistence: 'localStorage',
          batch_requests: true,
          batch_size: 50,
          batch_flush_interval_ms: 10000
        });
        this.mixpanelInitialized = true;
      } catch (error) {
        console.error('Failed to initialize Mixpanel:', error);
      }
    }
  }

  // Initialize comprehensive page tracking
  initPageTracking() {
    // Track page visibility changes
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
          this.trackPageExit();
        } else {
          this.trackPageReturn();
        }
      });

      // Track beforeunload for accurate exit tracking
      window.addEventListener('beforeunload', () => {
        this.trackPageExit();
      });
    }
  }

  // Push event to dataLayer for GTM
  pushEvent(eventName, parameters = {}) {
    if (window.dataLayer) {
      window.dataLayer.push({
        event: eventName,
        ...parameters
      });
    }
  }

  // Track event to both GTM and Mixpanel
  trackEvent(eventName, properties = {}) {
    // Push to GTM dataLayer
    this.pushEvent(eventName, properties);
    
    // Track in Mixpanel
    if (this.mixpanelInitialized) {
      try {
        mixpanel.track(eventName, {
          ...properties,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          user_agent: navigator.userAgent
        });
      } catch (error) {
        console.error('Mixpanel tracking error:', error);
      }
    }
  }

  // Track page views with comprehensive analytics
  trackPageView(pageName, additionalData = {}) {
    const now = Date.now();
    const pagePath = window.location.pathname;
    
    // Track time spent on previous page
    if (this.currentPage && this.pageStartTime) {
      const timeSpent = now - this.pageStartTime;
      this.trackPageTimeSpent(this.currentPage, timeSpent);
    }
    
    // Update page visit count
    this.pageVisitCounts[pagePath] = (this.pageVisitCounts[pagePath] || 0) + 1;
    
    // Add to page history
    this.pageHistory.push({
      page: pagePath,
      timestamp: now,
      visitNumber: this.pageVisitCounts[pagePath]
    });
    
    // Update current page tracking
    this.currentPage = pagePath;
    this.pageStartTime = now;
    
    const pageData = {
      page_name: pageName,
      page_path: pagePath,
      page_title: document.title,
      visit_number: this.pageVisitCounts[pagePath],
      session_duration: now - this.sessionStartTime,
      page_history_length: this.pageHistory.length,
      referrer: document.referrer,
      ...additionalData
    };

    // Push to GTM
    this.pushEvent('page_view', pageData);
    
    // Track in Mixpanel
    if (this.mixpanelInitialized) {
      try {
        mixpanel.track('Page View', {
          ...pageData,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Mixpanel page view error:', error);
      }
    }
  }

  // Track time spent on a specific page
  trackPageTimeSpent(pagePath, timeSpent) {
    const timeSpentSeconds = Math.round(timeSpent / 1000);
    
    if (this.mixpanelInitialized) {
      try {
        mixpanel.track('Page Time Spent', {
          page_path: pagePath,
          time_spent_seconds: timeSpentSeconds,
          time_spent_minutes: Math.round(timeSpentSeconds / 60 * 100) / 100,
          timestamp: new Date().toISOString()
        });
      } catch (error) {
        console.error('Mixpanel page time tracking error:', error);
      }
    }
  }

  // Track when user exits a page
  trackPageExit() {
    if (this.currentPage && this.pageStartTime) {
      const timeSpent = Date.now() - this.pageStartTime;
      this.trackPageTimeSpent(this.currentPage, timeSpent);
      
      if (this.mixpanelInitialized) {
        try {
          mixpanel.track('Page Exit', {
            page_path: this.currentPage,
            time_spent_seconds: Math.round(timeSpent / 1000),
            exit_method: 'visibility_change',
            timestamp: new Date().toISOString()
          });
        } catch (error) {
          console.error('Mixpanel page exit error:', error);
        }
      }
    }
  }

  // Track when user returns to a page
  trackPageReturn() {
    if (this.currentPage) {
      this.pageStartTime = Date.now();
      
      if (this.mixpanelInitialized) {
        try {
          mixpanel.track('Page Return', {
            page_path: this.currentPage,
            timestamp: new Date().toISOString()
          });
        } catch (error) {
          console.error('Mixpanel page return error:', error);
        }
      }
    }
  }

  // Get comprehensive page analytics for current user
  getPageAnalytics() {
    return {
      currentPage: this.currentPage,
      pageVisitCounts: this.pageVisitCounts,
      pageHistory: this.pageHistory,
      sessionDuration: Date.now() - this.sessionStartTime,
      totalPagesVisited: this.pageHistory.length
    };
  }

  // Track conversions
  trackConversion(conversionType, value = null) {
    const conversionData = {
      conversion_type: conversionType,
      value: value,
      timestamp: new Date().toISOString()
    };

    this.pushEvent('conversion', conversionData);
    
    if (this.mixpanelInitialized) {
      try {
        mixpanel.track('Conversion', conversionData);
      } catch (error) {
        console.error('Mixpanel conversion error:', error);
      }
    }
  }

  // Track user actions
  trackUserAction(action, details = {}) {
    const actionData = {
      action: action,
      ...details,
      timestamp: new Date().toISOString()
    };

    this.pushEvent('user_action', actionData);
    
    if (this.mixpanelInitialized) {
      try {
        mixpanel.track('User Action', actionData);
      } catch (error) {
        console.error('Mixpanel user action error:', error);
      }
    }
  }

  // Mixpanel-specific methods
  identifyUser(userId, userProperties = {}) {
    if (this.mixpanelInitialized) {
      try {
        mixpanel.identify(userId);
        if (Object.keys(userProperties).length > 0) {
          mixpanel.people.set(userProperties);
        }
      } catch (error) {
        console.error('Mixpanel identify error:', error);
      }
    }
  }

  setUserProperties(properties) {
    if (this.mixpanelInitialized) {
      try {
        mixpanel.people.set(properties);
      } catch (error) {
        console.error('Mixpanel set properties error:', error);
      }
    }
  }

  trackRevenue(amount, properties = {}) {
    if (this.mixpanelInitialized) {
      try {
        mixpanel.track('Revenue', {
          amount: amount,
          ...properties,
          timestamp: new Date().toISOString()
        });
        mixpanel.people.track_charge(amount);
      } catch (error) {
        console.error('Mixpanel revenue tracking error:', error);
      }
    }
  }

  // Product usage tracking methods
  trackFeatureUsage(feature, properties = {}) {
    this.trackEvent('Feature Used', {
      feature_name: feature,
      ...properties
    });
  }

  trackModuleAccess(module, properties = {}) {
    this.trackEvent('Module Accessed', {
      module_name: module,
      ...properties
    });
  }

  trackChatInteraction(action, properties = {}) {
    this.trackEvent('Chat Interaction', {
      action: action,
      ...properties
    });
  }

  trackDocumentUpload(documentType, properties = {}) {
    this.trackEvent('Document Uploaded', {
      document_type: documentType,
      ...properties
    });
  }

  trackSearchQuery(query, resultsCount = 0) {
    this.trackEvent('Search Performed', {
      query: query,
      results_count: resultsCount,
      query_length: query.length
    });
  }

  trackPlanUpgrade(fromPlan, toPlan, properties = {}) {
    this.trackEvent('Plan Upgraded', {
      from_plan: fromPlan,
      to_plan: toPlan,
      ...properties
    });
  }

  trackTeamInvitation(invitationType, properties = {}) {
    this.trackEvent('Team Invitation Sent', {
      invitation_type: invitationType,
      ...properties
    });
  }

  // Reset user (for logout)
  resetUser() {
    if (this.mixpanelInitialized) {
      try {
        mixpanel.reset();
      } catch (error) {
        console.error('Mixpanel reset error:', error);
      }
    }
  }
}

// Create singleton instance
const analyticsService = new AnalyticsService();
export default analyticsService;